import React, { createContext, useContext, useState } from "react";
import { format, differenceInCalendarDays } from "date-fns";
import { tr } from "date-fns/locale";
import {
  CalendarDays,
  User,
  Phone,
  Mail,
  BedDouble,
  Users,
  MessageSquare,
  Moon,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Calendar } from "./ui/calendar";
import { WhatsAppIcon, waUrl } from "./WhatsAppButton";
import { hotel, rooms } from "../mock";

const ReservationContext = createContext(null);

export const useReservation = () => {
  const ctx = useContext(ReservationContext);
  return ctx || { openReservation: () => {} };
};

const emptyForm = { name: "", phone: "", email: "", room: "", guests: "2", message: "" };

const fmt = (d) => (d ? format(d, "d MMMM yyyy", { locale: tr }) : null);

const Label = ({ children }) => (
  <label className="text-[11px] tracking-[0.18em] uppercase text-[#e7cf9c] mb-2 block">
    {children}
  </label>
);

const DateField = ({ placeholder, value, onChange, disabled, testid }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        data-testid={testid}
        className="u-field w-full flex items-center gap-2.5 pb-2.5 text-left text-[15px] text-white hover:border-white/40"
      >
        <CalendarDays size={16} className="text-[#d9b877]" />
        {value ? fmt(value) : <span className="text-white/45">{placeholder}</span>}
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" align="start">
      <Calendar
        mode="single"
        selected={value}
        onSelect={onChange}
        disabled={disabled}
        locale={tr}
        weekStartsOn={1}
        initialFocus
      />
    </PopoverContent>
  </Popover>
);

export const ReservationProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);

  const openReservation = (roomId) => {
    if (roomId && typeof roomId === "string") {
      setForm((f) => ({ ...f, room: roomId }));
    }
    setOpen(true);
  };

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const nights =
    checkIn && checkOut ? Math.max(differenceInCalendarDays(checkOut, checkIn), 0) : 0;

  const reset = () => {
    setForm(emptyForm);
    setCheckIn(null);
    setCheckOut(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.name.trim()) return toast.error("Lütfen adınızı ve soyadınızı giriniz.");
    if (!form.phone.trim()) return toast.error("Lütfen telefon numaranızı giriniz.");
    if (!checkIn) return toast.error("Lütfen giriş tarihini seçiniz.");
    if (!checkOut) return toast.error("Lütfen çıkış tarihini seçiniz.");
    if (nights < 1) return toast.error("Çıkış tarihi girişten sonra olmalıdır.");
    if (!form.room) return toast.error("Lütfen bir oda tipi seçiniz.");

    const roomName = rooms.find((r) => r.id === form.room)?.name || "Belirtilmedi";

    const lines = [
      "Merhaba, Safranbolu Çelebi Otel'de konaklama için rezervasyon talebim:",
      "",
      `• Ad Soyad: ${form.name}`,
      `• Telefon: ${form.phone}`,
    ];
    if (form.email.trim()) lines.push(`• E-posta: ${form.email}`);
    lines.push(
      `• Oda Tipi: ${roomName}`,
      `• Kişi Sayısı: ${form.guests} kişi`,
      `• Giriş: ${fmt(checkIn)}`,
      `• Çıkış: ${fmt(checkOut)}`,
      `• Konaklama: ${nights} gece`
    );
    if (form.message.trim()) lines.push(`• Not: ${form.message}`);
    lines.push("", "Müsaitlik ve fiyat bilgisi rica ediyorum.");

    window.open(waUrl(lines.join("\n")), "_blank", "noopener,noreferrer");
    toast.success("Talebiniz WhatsApp'a iletildi! Ekibimiz en kısa sürede dönüş yapacaktır.");
    reset();
    setOpen(false);
  };

  return (
    <ReservationContext.Provider value={{ openReservation }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="reservation-modal"
          className="max-w-lg glass-modal text-white p-0 overflow-hidden shadow-[0_40px_90px_-40px_rgba(0,0,0,0.85)] max-h-[92vh] flex flex-col gap-0"
        >
          {/* Header */}
          <div className="px-7 md:px-9 pt-8 pb-5 shrink-0">
            <DialogHeader>
              <p className="eyebrow text-[#d9b877] mb-2">Safranbolu Çelebi Otel</p>
              <DialogTitle className="font-display text-3xl md:text-4xl text-white font-500 leading-tight">
                Rezervasyon Talebi
              </DialogTitle>
            </DialogHeader>
            <p className="text-white/60 text-[13px] mt-2 font-body leading-relaxed">
              Bilgilerinizi eksiksiz doldurun; talebiniz tek dokunuşla WhatsApp
              üzerinden ekibimize iletilsin.
            </p>
            <div className="rule-h max-w-[120px] mt-5" />
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="px-7 md:px-9 pb-8 space-y-5 font-body overflow-y-auto"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>Ad Soyad *</Label>
                <div className="u-field flex items-center gap-2.5 pb-2.5">
                  <User size={16} className="text-[#d9b877]" />
                  <input
                    data-testid="res-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Adınız Soyadınız"
                    className="w-full bg-transparent outline-none text-[15px] text-white placeholder:text-white/40"
                  />
                </div>
              </div>
              <div>
                <Label>Telefon *</Label>
                <div className="u-field flex items-center gap-2.5 pb-2.5">
                  <Phone size={16} className="text-[#d9b877]" />
                  <input
                    data-testid="res-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="05xx xxx xx xx"
                    className="w-full bg-transparent outline-none text-[15px] text-white placeholder:text-white/40"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>E-posta</Label>
              <div className="u-field flex items-center gap-2.5 pb-2.5">
                <Mail size={16} className="text-[#d9b877]" />
                <input
                  data-testid="res-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="ornek@eposta.com (opsiyonel)"
                  className="w-full bg-transparent outline-none text-[15px] text-white placeholder:text-white/40"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>Giriş Tarihi *</Label>
                <DateField
                  testid="res-checkin"
                  placeholder="Tarih seçin"
                  value={checkIn}
                  onChange={(d) => {
                    setCheckIn(d);
                    if (checkOut && d && checkOut <= d) setCheckOut(null);
                  }}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </div>
              <div>
                <Label>Çıkış Tarihi *</Label>
                <DateField
                  testid="res-checkout"
                  placeholder="Tarih seçin"
                  value={checkOut}
                  onChange={setCheckOut}
                  disabled={(d) =>
                    d <= (checkIn || new Date(new Date().setHours(0, 0, 0, 0)))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label>Oda Tipi *</Label>
                <div className="u-field pb-2.5">
                  <Select value={form.room} onValueChange={(v) => update("room", v)}>
                    <SelectTrigger
                      data-testid="res-room"
                      className="!bg-transparent !border-0 !shadow-none !p-0 !h-auto text-[15px] text-white [&>svg]:text-white/60"
                    >
                      <div className="flex items-center gap-2.5">
                        <BedDouble size={16} className="text-[#d9b877]" />
                        <SelectValue placeholder="Oda seçiniz" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Kişi Sayısı</Label>
                <div className="u-field pb-2.5">
                  <Select value={form.guests} onValueChange={(v) => update("guests", v)}>
                    <SelectTrigger
                      data-testid="res-guests"
                      className="!bg-transparent !border-0 !shadow-none !p-0 !h-auto text-[15px] text-white [&>svg]:text-white/60"
                    >
                      <div className="flex items-center gap-2.5">
                        <Users size={16} className="text-[#d9b877]" />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {["1", "2", "3", "4", "5", "6"].map((g) => (
                        <SelectItem key={g} value={g}>
                          {g} Kişi
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label>Not / Talep</Label>
              <div className="u-field flex items-start gap-2.5 pb-2.5">
                <MessageSquare size={16} className="text-[#d9b877] mt-1" />
                <textarea
                  data-testid="res-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Özel isteklerinizi ekleyebilirsiniz (opsiyonel)"
                  rows={2}
                  className="w-full bg-transparent outline-none text-[15px] text-white placeholder:text-white/40 resize-none"
                />
              </div>
            </div>

            {/* Live summary */}
            {nights > 0 && (
              <div
                data-testid="res-summary"
                className="flex items-center justify-between gap-3 rounded-sm border border-[#d9b877]/25 bg-white/[0.05] px-4 py-3"
              >
                <span className="flex items-center gap-2 text-[13px] text-white/75">
                  <Moon size={15} className="text-[#d9b877]" />
                  {fmt(checkIn)} — {fmt(checkOut)}
                </span>
                <span className="font-display text-[#e7cf9c] text-[18px]">
                  {nights} gece
                </span>
              </div>
            )}

            <button
              type="submit"
              data-testid="res-submit"
              className="btn-gold w-full py-4 flex items-center justify-center gap-2.5 mt-1"
            >
              <WhatsAppIcon size={17} /> WhatsApp ile Gönder
            </button>

            <p className="text-center text-[11px] text-white/45 leading-relaxed">
              Bilgileriniz WhatsApp mesajı olarak iletilir. Bu bir taleptir; müsaitlik
              ve fiyat teyidi sonrası kesinleşir.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </ReservationContext.Provider>
  );
};

export default ReservationProvider;
